// What a reload would lose right now, so a waiting build is not applied
// under it (updatePolicy.ts). Screens hold with useUpdateHold(active): the
// chat composers while a message or a file is unsent or a reply streams
// in, the meeting recorder while it records or uploads. The page itself is
// also held while the field the user is in has text in it, or any text
// area does (a note half written on a screen that did not ask).
//
// Holds count across the dashboard's tabs (and an installed desktop app's
// windows): applying a build switches EVERY open tab to it, so a tab
// applies one only while no tab holds. A tab that holds keeps an entry in
// localStorage (HOLDS_KEY: tab -> when it last said so), written when its
// state changes and every HOLD_REFRESH_MS while it holds, removed when the
// tab goes away. An entry older than HOLD_TTL_MS is a tab that stopped
// running without saying so (a crash, or a background tab the browser
// froze) and counts for nothing; if a build takes over under such a tab,
// or under any tab that holds (Update pressed in another tab),
// PwaUpdatePrompt keeps that page and reloads it once nothing is lost.
// Without storage (blocked) holds count in their own tab only, and that
// last rule is what keeps a draft.
import { useEffect } from "react";

export const HOLDS_KEY = "lattice.pwa.updateHolds";
/** How often a tab that holds says so again (a hidden tab's timers run about once a minute). */
export const HOLD_REFRESH_MS = 30_000;
/** How long a tab's word that it holds lasts. */
export const HOLD_TTL_MS = 3 * 60_000;

/** This tab, as the other tabs know it. */
const TAB = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

let holds = 0;

/** Hold updates while `active`. */
export function useUpdateHold(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    holds += 1;
    shareHold();
    return () => {
      holds -= 1;
      shareHold();
    };
  }, [active]);
}

const TEXT_INPUT = /^(text|search|email|url|tel|password|number|)$/i;

/** Text the user has in the field they are in (focus alone loses nothing: an autofocused, empty login field). */
function typedText(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return (el.textContent ?? "").trim() !== "";
  if (el instanceof HTMLTextAreaElement) return el.value.trim() !== "";
  return el instanceof HTMLInputElement && TEXT_INPUT.test(el.type) && el.value.trim() !== "";
}

/** Whether reloading THIS tab now could lose something. */
export function heldHere(doc: Document = document): boolean {
  if (holds > 0) return true;
  if (typedText(doc.activeElement)) return true;
  return Array.from(doc.querySelectorAll("textarea")).some((t) => t.value.trim() !== "");
}

/** What the tabs wrote: tab -> when it last said it holds (ms). */
export type HoldBook = Record<string, number>;

/** An entry still in its time (either way: a clock set back does not make one last). */
function live(at: unknown, now: number): boolean {
  return typeof at === "number" && Math.abs(now - at) < HOLD_TTL_MS;
}

/** The other tabs that hold at `now`. */
export function holdingElsewhere(book: HoldBook, tab: string, now: number): string[] {
  return Object.keys(book).filter((t) => t !== tab && live(book[t], now));
}

/** `book` with `tab` holding (or not) at `now`, and the entries past their time gone. */
export function withHold(book: HoldBook, tab: string, held: boolean, now: number): HoldBook {
  const next: HoldBook = {};
  for (const [t, at] of Object.entries(book)) {
    if (t !== tab && live(at, now)) next[t] = at;
  }
  if (held) next[tab] = now;
  return next;
}

function readBook(): HoldBook {
  try {
    const book: unknown = JSON.parse(localStorage.getItem(HOLDS_KEY) ?? "{}");
    if (!book || typeof book !== "object" || Array.isArray(book)) return {};
    return Object.fromEntries(Object.entries(book).filter(([, at]) => typeof at === "number")) as HoldBook;
  } catch {
    return {};
  }
}

function writeBook(book: HoldBook): void {
  try {
    if (Object.keys(book).length) localStorage.setItem(HOLDS_KEY, JSON.stringify(book));
    else localStorage.removeItem(HOLDS_KEY);
  } catch {
    // blocked or full: holds count in this tab only
  }
}

let shared = false;
let sharedAt = 0;

/**
 * Tell the other tabs whether this one holds: when that changes, and every
 * HOLD_REFRESH_MS while it does. `gone`: the tab is going away.
 */
export function shareHold(gone = false): void {
  if (typeof document === "undefined") return;
  const now = Date.now();
  const held = !gone && heldHere();
  if (held === shared && (!held || now - sharedAt < HOLD_REFRESH_MS)) return;
  writeBook(withHold(readBook(), TAB, held, now));
  shared = held;
  sharedAt = now;
}

/** Whether applying a waiting build now could lose something, in this tab or another. */
export function updateHeld(doc: Document = document): boolean {
  return heldHere(doc) || holdingElsewhere(readBook(), TAB, Date.now()).length > 0;
}

/** For the self-test. */
export function holdCount(): number {
  return holds;
}
